import { useAction, useAtom } from '@reatom/npm-react'
import { useEffect, useMemo, useRef } from 'react'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { useTranslation } from '@root/shared/localization'
import { AnimateHeight } from '@root/shared/ui-kit'

import { reatomUpdateEmojiDrawer } from '../../atoms'
import { PremiumImageDrawerPreview } from '../update-emoji-drawer-preview'
import { UploadEmojiProgress } from '../update-emoji-progress'

import type { CurrencyModel } from '@root/entities/currency'
import type { PremiumMetaModel } from '@root/entities/premium'
import type { ClassNameProps } from '@root/shared/react'

type Props = {
  premiumMeta: PremiumMetaModel
  currency: CurrencyModel
  onClose?: () => void
} & ClassNameProps

export const UploadPremiumEmojiContent: React.FC<Props> = ({
  premiumMeta,
  currency,
  onClose,
  className,
}) => {
  const ref = useRef<HTMLInputElement>(null)
  const { t } = useTranslation()
  const [trendingEmoji] = useAtom(premiumMeta.trendingEmoji)

  const model = useMemo(
    () =>
      // eslint-disable-next-line @reatom/unit-naming-rule
      reatomUpdateEmojiDrawer({
        premiumMeta,
        currencyAddress: currency.address,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )
  const [requestState] = useAtom(model.updateRequestState)
  const [pollingState] = useAtom(model.pollingStateAtom)
  const [previewState] = useAtom(model.previewRequestStateAtom)

  const updateCurrency = useAction(model.updateCurrencyAction)
  const setImage = useAction(model.setImageAction)
  const reset = useAction(model.resetAction)

  const handleUpload = () =>
    ref.current?.dispatchEvent(new MouseEvent('click', { bubbles: true }))

  const handleClose = () => onClose?.()
  useEffect(() => {
    if (AsyncStateN.isSuccess(requestState)) {
      onClose?.()
    }
  }, [onClose, requestState])

  const handleReset = () => {
    reset()
    if (ref.current) {
      ref.current.value = ''
    }
  }

  const emojiId = (() => {
    if (
      AsyncStateN.isSuccess(previewState) &&
      AsyncStateN.isSuccess(pollingState)
    ) {
      return previewState.value.customEmojiId
    }

    return trendingEmoji?.emojiId
  })()

  return (
    <Root className={className}>
      <UploadEmojiProgress
        previewState={previewState}
        pollingState={pollingState}
        onReset={handleReset}
      />

      <AnimateHeight>
        <PremiumImageDrawerPreview
          onUploadClick={handleUpload}
          emojiId={emojiId}
          currency={currency}
        />
        <Input
          ref={ref}
          type="file"
          accept="image/*"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) {
              setImage(file)
            }
          }}
        />
      </AnimateHeight>

      <Action>
        <ButtonBack
          variant="secondary"
          radius="rounded"
          size="m"
          onClick={handleClose}
        >
          {t('common.cancel')}
        </ButtonBack>
        <Button
          radius="rounded"
          variant="glass"
          color="green"
          size="m"
          disabled={!AsyncStateN.isSuccess(pollingState)}
          busy={AsyncStateN.isLoading(requestState)}
          onClick={updateCurrency}
        >
          {t('common.confirm')}
        </Button>
      </Action>
    </Root>
  )
}

const Root = styled.div`
  display: grid;
`

const Action = styled.footer`
  display: grid;
  grid-template-columns: 1fr 1fr;

  gap: 10px;
  padding-top: 24px;
  padding-inline: 12px;
`

const ButtonBack = styled(Button)`
  color: var(--text-11);
`

const Input = styled.input`
  display: none;
`
